import type { AccessArea } from '@shared/types';
import { resolveAccessAreas } from './access';

/**
 * Single source for the app's navigation: area tabs (row 1) and their pages (row 2),
 * plus which access area / role unlocks each. Also feeds document titles and the Home shortcuts.
 */

export interface NavItem {
  to: string;
  label: string;
  /** NavLink `end` - match the path exactly (for an area's index page) */
  end?: boolean;
}

export type NavAreaKey = 'home' | 'tasks' | 'pj-kpi' | 'ricoh-spaces' | 'admin';

export interface NavArea {
  key: NavAreaKey;
  label: string;
  /** Landing page of the area (what the tab links to) */
  home: string;
  /** Access area required; undefined = any signed-in user */
  area?: AccessArea;
  ownerOnly?: boolean;
  items: NavItem[];
}

export const NAV_AREAS: NavArea[] = [
  { key: 'home', label: 'Home', home: '/', items: [] },
  {
    key: 'tasks',
    label: 'Tasks',
    home: '/dashboard',
    area: 'task-management',
    items: [
      { to: '/dashboard', label: 'Task Dashboard' },
      { to: '/tasks', label: 'Tasks' },
      { to: '/minutes', label: 'Minutes' },
      { to: '/import', label: 'Import' },
      { to: '/export', label: 'Export' },
      { to: '/screenshot-history', label: 'Screenshots' },
      { to: '/extension', label: 'Browser Extension' },
      { to: '/settings', label: 'Spaces & Tags' },
      { to: '/trash', label: 'Trash' },
    ],
  },
  {
    key: 'pj-kpi',
    label: 'Projects & KPIs',
    home: '/project-gantt',
    area: 'pj-kpi-management',
    items: [
      { to: '/project-gantt', label: 'Projects' },
      { to: '/kpi', label: 'KPIs' },
      { to: '/kpi-coverage', label: 'KPI Coverage' },
      { to: '/categories', label: 'Categories' },
    ],
  },
  {
    key: 'ricoh-spaces',
    label: 'Ricoh Spaces',
    home: '/ricoh-spaces',
    area: 'ricoh-spaces',
    items: [
      { to: '/ricoh-spaces', label: 'Overview', end: true },
      { to: '/ricoh-spaces/opportunities', label: 'Opportunities' },
      { to: '/ricoh-spaces/tasks', label: 'Action Items' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    home: '/users',
    ownerOnly: true,
    items: [{ to: '/users', label: 'Users' }],
  },
];

/** Pages outside the area tabs that still need a title. */
const EXTRA_TITLES: Record<string, string> = {
  '/': 'Home',
  '/profile': 'Profile',
  '/ui-kit': 'UI Kit',
  '/project-dashboard': 'Projects',
};

type NavUser = { role: 'owner' | 'member' | 'disable'; accessAreas?: AccessArea[] } | null | undefined;

/** Areas the user may enter, in display order. Home is always first for a signed-in user. */
export function visibleAreas(user: NavUser): NavArea[] {
  if (!user) return [];
  const areas = resolveAccessAreas(user);
  return NAV_AREAS.filter((a) => {
    if (a.key === 'home') return true;
    if (a.ownerOnly) return user.role === 'owner';
    return a.area ? areas.includes(a.area) : true;
  });
}

function pathMatches(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to.endsWith('/') ? to : `${to}/`);
}

/** The area a path belongs to (longest matching item wins). `/` → home; `/profile` → undefined. */
export function areaForPath(pathname: string): NavArea | undefined {
  if (pathname === '/') return NAV_AREAS[0];
  let best: { area: NavArea; len: number } | undefined;
  for (const area of NAV_AREAS) {
    for (const item of area.items) {
      if (pathMatches(pathname, item.to) && (!best || item.to.length > best.len)) best = { area, len: item.to.length };
    }
  }
  return best?.area;
}

/** Title for a path, from the nav labels (longest match) or the extras list. */
export function routeTitle(pathname: string): string | undefined {
  if (EXTRA_TITLES[pathname]) return EXTRA_TITLES[pathname];
  let best: { label: string; len: number } | undefined;
  for (const area of NAV_AREAS) {
    for (const item of area.items) {
      if (pathMatches(pathname, item.to) && (!best || item.to.length > best.len)) best = { label: item.label, len: item.to.length };
    }
  }
  return best?.label;
}
